import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";
import { useRoutePlan } from "../../features/routePlan/RoutePlanContext";
import { useVesselSettings } from "../../features/vesselSettings/VesselSettingsContext";
import { useChartSettings } from "../../features/chartSettings/ChartSettingsContext";
import {
  formatDistance,
  formatSpeedFromKnots,
  METERS_PER_NM,
} from "../../i18n/unitFormat";
import { trackEvent } from "../../utils/analytics";

function fmtEta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const mins = Math.round(seconds / 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export interface RoutePlanFooterProps {
  onStartDemo?: () => void;
}

export function RoutePlanFooter({ onStartDemo }: RoutePlanFooterProps) {
  const { t, i18n } = useTranslation();
  const routePlan = useRoutePlan();
  const { vesselSpeed } = useVesselSettings();
  const { useSiUnits } = useChartSettings();
  const locale = i18n.language;

  const routeDistance = routePlan.totalDistance || 0;
  const metersPerHour = vesselSpeed * METERS_PER_NM;
  const etaSeconds =
    routeDistance > 0 && metersPerHour > 0
      ? routeDistance / (metersPerHour / 3600)
      : 0;
  const canAnimate =
    !!onStartDemo &&
    routePlan.waypoints.length >= 2 &&
    routeDistance > 0;

  const handleStartDemo = () => {
    if (!canAnimate) return;
    try {
      trackEvent("feature_used", { feature: "demo_mode" });
    } catch {
      // ignore analytics failures; demo should still start
    }
    onStartDemo?.();
  };

  return (
    <AppBar
      position="static"
      color="transparent"
      elevation={0}
      sx={{
        background:
          "linear-gradient(180deg, #1C416A 0%, #163459 50%, #102743 100%)",
        backdropFilter: "saturate(140%) blur(8px)",
        WebkitBackdropFilter: "saturate(140%) blur(8px)",
        borderTop: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 -8px 20px rgba(0,0,0,0.15)",
        color: "#fff",
        width: "100%",
      }}
    >
      <Toolbar
        sx={{
          minHeight: 56,
          px: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Stack spacing={0.25}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, lineHeight: 1.1 }}
          >
            {routeDistance > 0
              ? formatDistance(routeDistance, locale, useSiUnits)
              : "—"}
          </Typography>
          {routeDistance > 0 && vesselSpeed > 0 && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {fmtEta(etaSeconds)}
              {" • "}
              {formatSpeedFromKnots(vesselSpeed, locale, useSiUnits)}
            </Typography>
          )}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" spacing={0.75} alignItems="center">
          <Tooltip title={t("menu.animate")}>
            <span>
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                aria-label={t("menu.animate")}
                onClick={handleStartDemo}
                disabled={!canAnimate}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  letterSpacing: 0.15,
                  minHeight: 32,
                  px: 1.25,
                  py: 0.4,
                  borderRadius: 2,
                  color: "#102743",
                  bgcolor: "#E8F3FF",
                  boxShadow:
                    "0 1px 0 rgba(255,255,255,0.45), 0 4px 12px rgba(0,0,0,0.22)",
                  "& .MuiButton-startIcon": {
                    mr: 0.5,
                    ml: -0.25,
                  },
                  "&:hover": {
                    bgcolor: "#fff",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255,255,255,0.45)",
                    bgcolor: "rgba(255,255,255,0.1)",
                    boxShadow: "none",
                  },
                }}
              >
                {t("menu.animate")}
              </Button>
            </span>
          </Tooltip>
          {routePlan.status === "loading" && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {t("routePlan.calculating")}
            </Typography>
          )}
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
